/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component } from 'react';
import SharedStyles from '../../_shared/styles';
import styles from './table-header.styles';
import Link from '../../_shared/link';
import Icon from '../../_shared/icon';
import { connect } from 'react-redux';
import { AppState } from '../../app.state';
import {
  filterEquipment,
  NONE,
  setEquipFiltersOpen,
  setEquipShowMoreFilters,
} from '../dashboard.actions';
import { DashEquipFilters } from '../dashboard.reducer';
import { get, sortedUniqBy } from 'lodash';
import { Model } from '../../_shared/interfaces/model';
import { Org } from '../../_shared/interfaces/org';
import { Modal } from 'antd';
import { SelectValue } from 'antd/lib/select';
import {
  userHasRole,
  getMyOrg,
  sortByLabel,
  isPB,
  getActiveModels,
  isDentalEzUser,
  isNativeAppBrowser,
  sendNativeEvent,
} from '../../utils';
import { StatesSelect } from '../../_shared/lib/states';
import { AIcon } from '../../_shared/icon';
import { WithAppConfig } from '../../_shared/hooks/useAppConfig';
import { Button, IProps as BtnProps } from '../../_shared/button';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { REG_KEY } from '../../registration';
import { Select } from '../../_shared/select';

interface IOpt {
  label: string;
  value: string | number;
}

interface Filter {
  opts: () => IOpt[];
  placeholder?: string;
  value?: string | string[];
  var: string;
  canDisplay?: () => boolean;
  style?: string;
  single?: boolean;
  type?: string;
  onClick?: () => void;
  title?: string;
  icon?: string;
  firstopts?: IOpt[];
}

interface IProps extends RouteComponentProps {
  selectedView: number;
  onSelectView: (index: number) => void;
  views: { icon: string }[];
  filterEquipment: (filters?: DashEquipFilters) => void;
  setEquipFiltersOpen: (open: boolean) => void;
  sState: AppState;
  equipFiltersOpen: boolean;
  equipmentCount: string;
  app?: any;
  // AER-1612 follow-up: the desktop Dashboard's "More Filters / Less
  // Filters" toggle (expanded vs. compressed filters mode) is now
  // persisted in Redux so it survives navigating to Equipment Details /
  // Edit Equipment and back. Mobile uses a Modal driven by
  // `equipFiltersOpen` and is unaffected.
  equipShowMoreFilters: boolean;
  setEquipShowMoreFilters: (open: boolean) => void;
}

interface IState {
  filters_1: Filter[];
  filters_2: Filter[];
}

export class _TableHeader extends Component<IProps> {
  static defaultProps: IProps;
  state: IState = {
    filters_1: [
      {
        opts: () =>
          getActiveModels()
            .map((m: Model) => ({
              label: m.id,
              value: m.id,
            }))
            .sort(sortByLabel),
        placeholder: 'Model',
        var: 'model',
        value: 'dash.equipmentFilters.model',
        canDisplay: () => isDentalEzUser(this.props.sState),
      },
      {
        opts: () =>
          sortedUniqBy(
            getActiveModels(true)
              .map((m: Model) => ({
                label: m.displayName,
                value: m.displayName,
              }))
              .sort(sortByLabel),
            m => m.label
          ),
        placeholder: 'Model',
        var: 'modelDisplayName',
        value: 'dash.equipmentFilters.modelDisplayName',
        canDisplay: () => !isDentalEzUser(this.props.sState),
      },
      {
        canDisplay: () => userHasRole([0, 1, 7], this.props.sState),
        opts: () => [
          ...get(this.props.sState, 'dash.orgs', [])
            .filter((o: Org) => o.orgType === 0)
            .map((m: Org) => ({ label: m.name, value: m.id }))
            .sort(sortByLabel),
        ],
        firstopts: [{ label: 'No Service Org', value: NONE }],
        placeholder: 'Service Org',
        var: 'serviceOrg',
        value: 'dash.equipmentFilters.serviceOrg',
        style: `width: 300px;`,
      },
      {
        canDisplay: () => {
          const isSuper = userHasRole([0, 1, 7], this.props.sState);
          const mOrg = getMyOrg(this.props.sState);
          if (isSuper) {
            return true;
          } else {
            if (mOrg && mOrg.orgType === 0) {
              return true;
            }
            return false;
          }
        },
        opts: () => {
          return [
            ...get(this.props.sState, 'dash.orgs', [])
              .filter((o: Org) => o.orgType === 1)
              .map((m: Org) => ({ label: m.name, value: m.id }))
              .sort(sortByLabel),
          ];
        },
        firstopts: [{ label: 'No Dental Org', value: NONE }],
        placeholder: 'Dental Org',
        var: 'dentalOrg',
        value: 'dash.equipmentFilters.dentalOrg',
        style: `width: 300px;`,
      },
      {
        canDisplay: () => true,
        opts: () => {
          const isSuper = userHasRole([0, 1, 7], this.props.sState);
          const opts = [
            // { label: 'No Location', value: NONE },
            ...get(this.props.sState, 'dash.locations', [])
              .map((m: Org) => ({
                label: m.name,
                value: m.id,
                orgId: m.orgId,
              }))
              .sort(sortByLabel),
          ];

          if (isSuper) {
            return opts;
          } else {
            const orgIds: string[] = [];
            get(this.props.sState, 'dash.orgs', []).map((o: Org) => {
              orgIds.push(o.id);
              if (o.serviceOrgId) {
                orgIds.push(o.serviceOrgId);
              }
            });
            let locs: any = get(this.props.sState, 'auth.user.locations', []);
            if (!Array.isArray(locs)) {
              locs = [];
            }
            return opts.filter(
              (o: any) =>
                locs.indexOf(o.value) !== -1 ||
                orgIds.indexOf(o.orgId) !== -1 ||
                o.value === NONE
            );
          }
        },
        placeholder: 'Location',
        var: 'location',
        firstopts: [{ label: 'No Location', value: NONE }],
        value: 'dash.equipmentFilters.location',
        style: `width: 300px;`,
      },
    ],
    filters_2: [
      {
        canDisplay: () => userHasRole([0], this.props.sState),
        opts: () => [
          { label: 'True', value: 1 },
          { label: 'False', value: 0 },
          { label: 'None', value: -1 },
        ],
        placeholder: 'Show On Dash',
        var: 'showOnDash',
        value: 'dash.equipmentFilters.showOnDash',
        single: true,
      },
      {
        opts: () => [
          { label: 'On', value: 'on' },
          { label: 'Standby', value: 'standby' },
          { label: 'Offline', value: 'off' },
        ],
        placeholder: 'Status',
        var: 'status',
        value: 'dash.equipmentFilters.status',
      },
      {
        canDisplay: () => userHasRole([0, 1, 7], this.props.sState),
        opts: () => [
          { label: 'Maintenance', value: 'maint' },
          { label: 'High Pressure', value: 'highPressure' },
          { label: 'Debug', value: 'debug' },
          { label: 'Power Up Override', value: 'highVoltage' },
        ],
        placeholder: 'Mode',
        var: 'mode',
        value: 'dash.equipmentFilters.mode',
        style: `width: 230px;`,
      },
      {
        opts: () => [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }],
        placeholder: 'Consignment',
        var: 'onConsignment',
        value: 'dash.equipmentFilters.onConsignment',
        single: true,
      },
      {
        canDisplay: () => userHasRole([0, 1], this.props.sState),
        opts: () =>
          (get(this, 'props.sState.dash.integrations', []) || []).filter((f: any) => get(f, 'frontend.showInFilter', true)).map((i: {}) => ({
            label: get(i, 'sk'),
            value: get(i, 'sk'),
          })) as any[],
        placeholder: 'External Integrations',
        var: 'integrations',
        value: 'dash.equipmentFilters.integrations',
      },
      {
        opts: () => StatesSelect(),
        placeholder: 'State',
        var: 'dentalOrgState',
        value: 'dash.equipmentFilters.dentalOrgState',
      },
      // {
      //   opts: () => [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }],
      //   placeholder: 'Active',
      //   var: 'isActive',
      //   value: 'dash.equipmentFilters.isActive',
      //   single: true,
      // },
    ],
  };

  goToReg = () => {
    localStorage.removeItem(REG_KEY);
    // AER-1589: match the unauthenticated and menu-accessed "Register
    // Equipment" buttons — in the native app webview, hand off to the
    // camera QR scanner first instead of jumping straight to manual entry.
    if (isNativeAppBrowser()) {
      sendNativeEvent({ action: 'router', data: '/setupwifi' });
      return;
    }
    this.props.history.push('/eqRegistration');
  };

  componentDidMount(): void {
    const { sState } = this.props;
    if (userHasRole([2, 3, 4, 6], sState) && !get(sState, 'auth.user.orgId')) {
      // AER-1589: org-less users still need to land on the manual
      // registration form to create their org — don't reroute them through
      // the camera scanner here.
      localStorage.removeItem(REG_KEY);
      this.props.history.push('/eqRegistration');
    }
  }

  shouldComponentUpdate(nextProps: IProps, nextState: IState) {
    if (nextProps.selectedView !== this.props.selectedView) {
      return true;
    } else if (nextProps.views !== this.props.views) {
      return true;
    } else if (nextProps.filterEquipment !== this.props.filterEquipment) {
      return true;
    } else if (
      // AER-1612 follow-up: showMoreFilters moved from local state to
      // Redux props; rerender whenever the persisted value flips.
      nextProps.equipShowMoreFilters !== this.props.equipShowMoreFilters
    ) {
      return true;
    } else if (
      nextProps.sState.dash.equipmentFilters !==
      this.props.sState.dash.equipmentFilters
    ) {
      return true;
    } else if (nextProps.equipFiltersOpen !== this.props.equipFiltersOpen) {
      return true;
    } else if (nextProps.equipmentCount !== this.props.equipmentCount) {
      return true;
    } else if (
      get(nextProps, 'sState.dash.view.isPhoneBreak') !==
      get(this.props, 'sState.dash.view.isPhoneBreak')
    ) {
      return true;
    }
    return false;
  }

  changeSelect = (val: SelectValue, index: number, filter: Filter[]) => {
    const { sState, filterEquipment } = this.props;

    const select = filter[index];

    filterEquipment({
      ...get(sState, 'dash.equipmentFilters', {}),
      [select.var]: val,
    });
  };
  clearFilters = () => {
    const { filterEquipment } = this.props;
    filterEquipment();
  };
  changeView = (i: number) => {
    const { onSelectView } = this.props;
    onSelectView && onSelectView(i);
  };
  renderFilters = (
    filters: Filter[],
    shouldRenderButtons: boolean,
    shouldRenderClear: boolean
  ) => {
    // AER-1612 follow-up: read from Redux-backed props instead of local
    // component state so the expanded/compressed filter mode survives
    // unmount.
    const { sState, equipShowMoreFilters: showMoreFilters } = this.props;

    const isPhoneBreak = isPB(sState);

    const Filters = filters.map((filter, i) => {
      const canDisplay = filter.canDisplay ? filter.canDisplay() : true;
      const opts =
        typeof filter.opts === 'function' ? filter.opts() : filter.opts;

      if (!canDisplay) {
        return null;
      }

      if (filter.type === 'button') {
        return <Button css={css(styles.margRight, filter.style)} onClick={() => filter.onClick && filter.onClick()} title={filter.title} icon={filter.icon} />
      }
      return (
        <Select
          key={i}
          css={css(styles.select, styles.margRight, filter.style)}
          placeholder={filter.placeholder}
          value={get(sState, filter.value || '', [])}
          mode={filter.single ? undefined : 'multiple'}
          onChange={(val: SelectValue) => this.changeSelect(val, i, filters)}
          filterOption={(input, option) => {
            const name = option!.props.children as string;
            if (name)
              return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            return true;
          }}
        >
          {[...(filter.firstopts || []), ...opts].map((item, oi) => (
            <Select.Option key={oi} value={item.value}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
      );
    });

    return isPhoneBreak ? (
      // In phone-break mode, the filters render inside a Modal opened from
      // render() — no need to gate on showFilters here, the Modal controls
      // visibility.
      <div>
        <div css={css(styles.pbFilters)}>{Filters}</div>

        {shouldRenderClear && (
          <Link css={css(styles.link)} onClick={this.clearFilters}>
            Clear
          </Link>
        )}
      </div>
    ) : (
      <div css={css(styles.container, SharedStyles.row)}>
        {shouldRenderButtons && this.renderRegister()}
        {Filters}

        {shouldRenderButtons && (
          <Link css={css(styles.link)} onClick={this.clearFilters}>
            Clear
          </Link>
        )}
        {shouldRenderButtons && (
          <Link
            css={css(SharedStyles.row, styles.link)}
            onClick={() =>
              this.props.setEquipShowMoreFilters(!showMoreFilters)
            }
          >
            {`${showMoreFilters ? 'Less Filters' : 'More Filters'}`}
            <AIcon
              css={css('margin-left: 5px; font-size: 12px;')}
              type={showMoreFilters ? 'up' : 'down'}
            />
          </Link>
        )}

        {shouldRenderButtons && this.renderViews()}
      </div>
    );
  };
  renderRegister = () => {
    if (userHasRole([5, 6, 7], this.props.sState)) return null;
    return (
      <Button
        css={css(styles.margRight)}
        onClick={this.goToReg}
        title={'Register Equipment'}
        icon={'plus'}
      />
    );
  };
  renderViews = () => {
    const { selectedView, views, app } = this.props;

    return (
      <div css={css(styles.viewContainer, SharedStyles.row)}>
        {views.map((view, i) => (
          <div
            key={i}
            onClick={() => this.changeView(i)}
            css={css(styles.viewIcon)}
          >
            <Icon
              name={view.icon}
              color={app.getColor(
                i === selectedView ? 'primary' : 'disabledColor'
              )}
              size={18}
            />
          </div>
        ))}
      </div>
    );
  };
  render() {
    const { sState } = this.props;
    const isPhoneBreak = isPB(sState);

    if (isPhoneBreak) {
      // Mobile: present filters as a modal overlay rather than an inline
      // sticky bar — keeps the filter UI accessible regardless of how far
      // the user has scrolled.
      return (
        <Modal
          title={'Filters'}
          open={!!this.props.equipFiltersOpen}
          onCancel={() => this.props.setEquipFiltersOpen(false)}
          footer={null}
          destroyOnHidden={false}
        >
          {this.renderFilters(this.state.filters_1, true, false)}
          {this.renderFilters(this.state.filters_2, false, true)}
        </Modal>
      );
    }

    return (
      <div css={css(styles.filtersContainer)}>
        {this.renderFilters(this.state.filters_1, true, true)}
        {this.props.equipShowMoreFilters &&
          this.renderFilters(this.state.filters_2, false, false)}
      </div>
    );
  }
}

_TableHeader.defaultProps = ({
  selectedView: 0,
  onSelectView: () => 0,
  views: [],
  sState: {} as AppState,
  filterEquipment: () => 0,
  setEquipFiltersOpen: () => 0,
  equipFiltersOpen: false,
  equipmentCount: '',
  // AER-1612 follow-up: default the new Redux-backed showMoreFilters
  // toggle to closed (compressed) when the prop hasn't been wired.
  equipShowMoreFilters: false,
  setEquipShowMoreFilters: () => 0,
} as unknown) as IProps;

export const TableHeader = WithAppConfig(
  connect(
    (sState: AppState) => ({
      sState,
      equipFiltersOpen: !!get(sState, 'dash.view.equipFiltersOpen'),
      // AER-1612 follow-up: pull the persisted More/Less Filters toggle out
      // of `dash.view` so it survives a remount of <TableHeader>.
      equipShowMoreFilters: !!get(sState, 'dash.view.equipShowMoreFilters'),
    }),
    {
      filterEquipment,
      setEquipFiltersOpen,
      setEquipShowMoreFilters,
    }
  )(withRouter(_TableHeader) as any)
) as unknown as typeof _TableHeader;
